import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchContactMessages,
  fetchJobApplications,
  updateContactStatus,
  updateJobStatus
} from '../../lib/adminApi';
import { useAdminAuth } from '../../context/AdminAuthContext';

type ContactStatus = 'new' | 'in_progress' | 'closed';
type JobStatus = 'new' | 'reviewing' | 'contacted' | 'rejected';

interface ContactMessage {
  id: number;
  name: string;
  email: string;
  subject: string;
  message: string;
  status: ContactStatus;
  created_at: string;
}

interface JobApplication {
  id: number;
  job_id: string;
  job_title: string;
  name: string;
  email: string;
  phone: string;
  cover_letter: string;
  resume_path: string;
  resume_original_name: string;
  status: JobStatus;
  created_at: string;
}

const contactStatuses: ContactStatus[] = ['new', 'in_progress', 'closed'];
const jobStatuses: JobStatus[] = ['new', 'reviewing', 'contacted', 'rejected'];

const AdminManagementPage: React.FC = () => {
  const { token, logout } = useAdminAuth();
  const [contacts, setContacts] = useState<ContactMessage[]>([]);
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    setIsLoading(true);
    Promise.all([fetchContactMessages(token), fetchJobApplications(token)])
      .then(([contactsResponse, jobsResponse]) => {
        setContacts(contactsResponse.data);
        setApplications(jobsResponse.data);
      })
      .catch(err => setError(err.message))
      .finally(() => setIsLoading(false));
  }, [token]);

  const handleContactStatusChange = async (id: number, status: ContactStatus) => {
    if (!token) return;
    try {
      await updateContactStatus(token, id, status);
      setContacts(prev => prev.map(item => (item.id === id ? { ...item, status } : item)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update contact');
    }
  };

  const handleJobStatusChange = async (id: number, status: JobStatus) => {
    if (!token) return;
    try {
      await updateJobStatus(token, id, status);
      setApplications(prev => prev.map(item => (item.id === id ? { ...item, status } : item)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update application');
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 py-8">
      <div className="max-w-7xl mx-auto">
        <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-10">
          <div>
            <p className="text-sm text-slate-400 uppercase tracking-wide">Admin Console</p>
            <h1 className="text-3xl font-bold">Manage Submissions</h1>
          </div>
          <div className="flex gap-3">
            <Link
              to="/admin/dashboard"
              className="px-4 py-2 rounded-full border border-white/20 hover:border-white transition"
            >
              Back to Dashboard
            </Link>
            <button
              onClick={logout}
              className="px-4 py-2 rounded-full bg-red-500/80 hover:bg-red-500 transition"
            >
              Logout
            </button>
          </div>
        </header>

        {error && <p className="text-red-400 mb-4">{error}</p>}
        {isLoading && <p className="text-slate-400">Loading latest submissions...</p>}

        <section className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold">Contact Messages</h2>
            <span className="text-sm text-slate-400">Showing latest {contacts.length}</span>
          </div>
          <div className="overflow-x-auto rounded-3xl border border-white/10">
            <table className="w-full text-left text-sm">
              <thead className="bg-white/[0.04] text-slate-300">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Subject</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Received</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map(contact => (
                  <tr key={contact.id} className="border-t border-white/[0.05]">
                    <td className="px-4 py-3">{contact.name}</td>
                    <td className="px-4 py-3">{contact.email}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium">{contact.subject}</p>
                      <p className="text-slate-400 text-xs max-w-sm truncate">{contact.message}</p>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={contact.status}
                        onChange={e => handleContactStatusChange(contact.id, e.target.value as ContactStatus)}
                        className="bg-slate-900 border border-white/10 rounded-full px-3 py-1 text-xs uppercase tracking-wide"
                      >
                        {contactStatuses.map(status => (
                          <option key={status} value={status}>
                            {status.replace('_', ' ')}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs">
                      {new Date(contact.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
                {!contacts.length && !isLoading && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-slate-500">
                      No contact messages yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-semibold">Job Applications</h2>
            <span className="text-sm text-slate-400">Showing latest {applications.length}</span>
          </div>
          <div className="overflow-x-auto rounded-3xl border border-white/10">
            <table className="w-full text-left text-sm">
              <thead className="bg-white/[0.04] text-slate-300">
                <tr>
                  <th className="px-4 py-3">Candidate</th>
                  <th className="px-4 py-3">Job</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Applied</th>
                </tr>
              </thead>
              <tbody>
                {applications.map(app => (
                  <tr key={app.id} className="border-t border-white/[0.05]">
                    <td className="px-4 py-3">
                      <p className="font-medium">{app.name}</p>
                      <p className="text-xs text-slate-400">{app.resume_original_name}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium">{app.job_title}</p>
                      <p className="text-xs text-slate-400">{app.job_id}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-300">
                      <div>{app.email}</div>
                      <div className="text-xs text-slate-400">{app.phone}</div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={app.status}
                        onChange={e => handleJobStatusChange(app.id, e.target.value as JobStatus)}
                        className="bg-slate-900 border border-white/10 rounded-full px-3 py-1 text-xs uppercase tracking-wide"
                      >
                        {jobStatuses.map(status => (
                          <option key={status} value={status}>
                            {status}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs">
                      {new Date(app.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
                {!applications.length && !isLoading && (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-slate-500">
                      No applications yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminManagementPage;

